import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { useCreateNode } from "../../api/hooks.js";
import type { CreateNodeBody, DriverType } from "../../api/types.js";
import { Button, Input, Label, Select } from "../../components/ui.js";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog.js";
import { toast } from "../../components/toast.js";

const DRIVERS: DriverType[] = ["mediamtx", "srs", "cloudflare"];

const DRIVER_HINT: Record<DriverType, string> = {
  mediamtx:
    "MediaMTX: SRT + RTMP ingest, WHEP/LL-HLS/HLS playback. RTMPS requires a cert.",
  srs: "SRS: SRT + RTMP ingest, plus HTTP/WS-FLV playback. RTMPS not native.",
  cloudflare:
    "Cloudflare Stream: SaaS, no host. Enter this account's id + API token — the webhook is auto-subscribed and its secret stored for you. Each CF node is one CF account.",
};

export function RegisterNodeModal({ onClose }: { onClose: () => void }) {
  const createNode = useCreateNode();
  const [driver, setDriver] = useState<DriverType>("mediamtx");
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [region, setRegion] = useState("");
  const [publicHost, setPublicHost] = useState("");
  const [scheme, setScheme] = useState("https");
  const [apiBaseUrl, setApiBaseUrl] = useState("");
  const [srtPort, setSrtPort] = useState("8890");
  const [rtmpPort, setRtmpPort] = useState("1935");
  const [hlsPort, setHlsPort] = useState("8888");
  const [whepPort, setWhepPort] = useState("8889");
  // Cloudflare account credentials (one CF node = one CF account). The webhook secret is not
  // entered — it's auto-generated by subscribing the account's Stream webhook at registration.
  const [accountId, setAccountId] = useState("");
  const [apiToken, setApiToken] = useState("");

  const [token, setToken] = useState<string | null>(null);
  const [webhookUrl, setWebhookUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const isCf = driver === "cloudflare";
  const cfMissing = isCf && (!accountId.trim() || !apiToken.trim());

  function num(v: string): number | undefined {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : undefined;
  }

  async function submit() {
    const body: CreateNodeBody = {
      slug: slug.trim(),
      name: name.trim() || slug.trim(),
      region: region.trim(),
      driver,
    };
    if (isCf) {
      body.accountId = accountId.trim();
      body.apiToken = apiToken.trim();
    } else {
      body.publicHost = publicHost.trim();
      body.scheme = scheme;
      if (apiBaseUrl.trim()) body.apiBaseUrl = apiBaseUrl.trim();
      const srt = num(srtPort);
      const rtmp = num(rtmpPort);
      body.ingest = {
        ...(srt ? { srt: { port: srt } } : {}),
        ...(rtmp ? { rtmp: { port: rtmp } } : {}),
      };
      body.playback = {
        ...(num(hlsPort) ? { hlsPort: num(hlsPort) } : {}),
        ...(num(whepPort) ? { whepPort: num(whepPort) } : {}),
      };
    }
    try {
      const result = await createNode.mutateAsync(body);
      setToken(result.nodeToken);
      setWebhookUrl(result.node.config.cf?.webhookUrl ?? null);
      toast("Node registered", "success");
    } catch {
      // toasted globally
    }
  }

  async function copyValue(value: string) {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        aria-describedby={undefined}
        className="scrollbar-thin max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>Register node</DialogTitle>
        </DialogHeader>

        {token || webhookUrl ? (
          <div className="space-y-4">
            {webhookUrl ? (
              <>
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-200">
                  Cloudflare node created and its Stream webhook was
                  auto-subscribed (signing secret stored). Nothing to paste —
                  this URL is just for reference:
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 break-all rounded-lg border border-white/10 bg-black/40 px-3 py-2 font-mono text-xs text-foreground/90">
                    {webhookUrl}
                  </code>
                  <Button variant="secondary" onClick={() => copyValue(webhookUrl)}>
                    {copied ? (
                      <Check className="h-4 w-4 text-emerald-400" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-200">
                  Copy the node token now — it is shown only once. The operator
                  puts it in the node's docker-compose as{" "}
                  <code className="font-mono">x-node-token</code>.
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 break-all rounded-lg border border-white/10 bg-black/40 px-3 py-2 font-mono text-xs text-foreground/90">
                    {token}
                  </code>
                  <Button variant="secondary" onClick={() => token && copyValue(token)}>
                    {copied ? (
                      <Check className="h-4 w-4 text-emerald-400" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </>
            )}
            <div className="flex justify-end">
              <Button variant="primary" onClick={onClose}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label>Driver</Label>
              <Select
                value={driver}
                onChange={(e) => setDriver(e.target.value as DriverType)}
              >
                {DRIVERS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
              <p className="mt-1.5 text-xs text-muted-foreground">
                {DRIVER_HINT[driver]}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label>Slug</Label>
                <Input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="hk-srs-1"
                />
              </div>
              <div>
                <Label>Region</Label>
                <Input
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  placeholder="hk"
                />
              </div>
            </div>

            <div>
              <Label>Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Hong Kong SRS #1"
              />
            </div>

            {isCf && (
              <div className="space-y-3 rounded-xl border border-white/[0.06] bg-black/20 p-4">
                <div>
                  <Label>Account ID</Label>
                  <Input
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value)}
                    autoComplete="off"
                    placeholder="Cloudflare account id"
                  />
                </div>
                <div>
                  <Label>API token</Label>
                  <Input
                    value={apiToken}
                    onChange={(e) => setApiToken(e.target.value)}
                    type="password"
                    autoComplete="new-password"
                    placeholder="Stream:Edit API token"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Stored on this node only (one CF node = one CF account). On
                  save, the account's Stream webhook is auto-subscribed and its
                  signing secret stored — you don't enter or paste anything.
                </p>
              </div>
            )}

            {!isCf && (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="sm:col-span-2">
                    <Label>Public host</Label>
                    <Input
                      value={publicHost}
                      onChange={(e) => setPublicHost(e.target.value)}
                      placeholder="hk1.example.com"
                    />
                  </div>
                  <div>
                    <Label>Scheme</Label>
                    <Select
                      value={scheme}
                      onChange={(e) => setScheme(e.target.value)}
                    >
                      <option value="https">https</option>
                      <option value="http">http</option>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>API base URL (reconcile)</Label>
                  <Input
                    value={apiBaseUrl}
                    onChange={(e) => setApiBaseUrl(e.target.value)}
                    placeholder="http://hk1.example.com:9997"
                  />
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <Label>SRT port</Label>
                    <Input
                      value={srtPort}
                      onChange={(e) => setSrtPort(e.target.value)}
                      inputMode="numeric"
                    />
                  </div>
                  <div>
                    <Label>RTMP port</Label>
                    <Input
                      value={rtmpPort}
                      onChange={(e) => setRtmpPort(e.target.value)}
                      inputMode="numeric"
                    />
                  </div>
                  <div>
                    <Label>HLS port</Label>
                    <Input
                      value={hlsPort}
                      onChange={(e) => setHlsPort(e.target.value)}
                      inputMode="numeric"
                    />
                  </div>
                  <div>
                    <Label>WHEP port</Label>
                    <Input
                      value={whepPort}
                      onChange={(e) => setWhepPort(e.target.value)}
                      inputMode="numeric"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={submit}
                loading={createNode.isPending}
                disabled={!slug.trim() || !region.trim() || cfMissing}
              >
                Register
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
